import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { CastingProvider } from "@/context/CastingContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CastingBar } from "@/components/CastingBar";
import { CustomCursor } from "@/components/CustomCursor";
import { CookieBanner } from "@/components/CookieBanner";
import Home from "@/pages/Home";
import Models from "@/pages/Models";
import Actors from "@/pages/Actors";
import Voice from "@/pages/Voice";
import Discover from "@/pages/Discover";
import NewFaces from "@/pages/NewFaces";
import Submissions from "@/pages/Submissions";
import About from "@/pages/About";
import Clients from "@/pages/Clients";
import Contact from "@/pages/Contact";
import TalentProfile from "@/pages/TalentProfile";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian font-mono text-xs uppercase tracking-[0.3em] text-white/40">
        Verifying session…
      </div>
    );
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
};

const Chrome = ({ children }) => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <Navbar />}
      {children}
      {!isAdmin && <Footer />}
      {!isAdmin && <CastingBar />}
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
        <CastingProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-obsidian font-body text-ivory antialiased">
            <CustomCursor />
            <ScrollToTop />
            <Chrome>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/models" element={<Models />} />
                <Route path="/actors" element={<Actors />} />
                <Route path="/voice" element={<Voice />} />
                <Route path="/discover" element={<Discover />} />
                <Route path="/new-faces" element={<NewFaces />} />
                <Route path="/submissions" element={<Submissions />} />
                <Route path="/about" element={<About />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/talent/:id" element={<TalentProfile />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Chrome>
            <CookieBanner />
            <div data-testid="toast-notification">
              <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: "#121212",
                    border: "1px solid rgba(212,175,55,0.35)",
                    color: "#FAF8F5",
                    borderRadius: "2px",
                  },
                }}
              />
            </div>
          </div>
        </BrowserRouter>
        </CastingProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
